import { Camera, ImagePlus, Loader2, Upload, X } from "lucide-react"
import {
  ChangeEvent,
  DragEvent,
  KeyboardEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react"
import { cn } from "~/common/cn"
import { normalizeImage } from "~/common/normalize-image"
import { Button } from "~/ui/button"

const DEFAULT_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif"

type Shape = "card" | "circle"

export type PhotoDropzoneProps = {
  /** Currently-selected file (controlled) */
  file?: File | null
  /** A pre-existing image URL (e.g. a saved avatar) shown when no `file` is set */
  initialUrl?: string | null
  /** Called when the user picks a new file */
  onFileSelected: (file: File) => void
  /** Called when the user clears the current selection. Hide the remove button if not provided. */
  onRemove?: () => void
  /** "card" = wide rectangle with hint text; "circle" = compact circular avatar */
  shape?: Shape
  /** Aspect ratio for the card variant. Defaults to 4/3. */
  aspectRatio?: string
  /** Accept attribute for the file input */
  accept?: string
  /** Disable interaction */
  disabled?: boolean
  /** Optional accessible label for the dropzone */
  label?: string
  className?: string
}

/**
 * Drag-and-drop image picker with a click-to-choose fallback and a separate
 * "Take a picture" button shown on touch devices. All three paths emit a File
 * via onFileSelected.
 */
export const PhotoDropzone = ({
  file,
  initialUrl,
  onFileSelected,
  onRemove,
  shape = "card",
  aspectRatio = "4 / 3",
  accept = DEFAULT_ACCEPT,
  disabled = false,
  label = "Add a photo",
  className,
}: PhotoDropzoneProps) => {
  const id = useId()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const cameraInputRef = useRef<HTMLInputElement | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file])
  useEffect(() => {
    if (!previewUrl) return
    return () => URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const displayUrl = previewUrl ?? (file ? null : initialUrl) ?? null
  const hasImage = displayUrl != null

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return
    const f = fileList[0]
    const isHeicByName = /\.(heic|heif)$/i.test(f.name)
    if (!f.type.startsWith("image/") && !isHeicByName) return

    setIsProcessing(true)
    try {
      const normalized = await normalizeImage(f)
      onFileSelected(normalized)
    } catch (err) {
      console.error("Image normalization failed; using original file", err)
      onFileSelected(f)
    } finally {
      setIsProcessing(false)
    }
  }

  const interactionDisabled = disabled || isProcessing

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    void handleFiles(e.target.files)
    e.target.value = ""
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    if (interactionDisabled) return
    void handleFiles(e.dataTransfer.files)
  }

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!interactionDisabled) setIsDragging(true)
  }

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (interactionDisabled) return
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      fileInputRef.current?.click()
    }
  }

  const openPicker = () => {
    if (!interactionDisabled) fileInputRef.current?.click()
  }
  const openCamera = () => {
    if (!interactionDisabled) cameraInputRef.current?.click()
  }

  const isCircle = shape === "circle"

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        id={`${id}-file`}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={onChange}
        disabled={interactionDisabled}
      />
      <input
        ref={cameraInputRef}
        id={`${id}-camera`}
        type="file"
        accept={accept}
        capture="environment"
        className="sr-only"
        onChange={onChange}
        disabled={interactionDisabled}
      />

      <div
        role="button"
        tabIndex={interactionDisabled ? -1 : 0}
        aria-label={label}
        aria-busy={isProcessing || undefined}
        aria-disabled={interactionDisabled || undefined}
        onClick={openPicker}
        onKeyDown={onKeyDown}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={cn(
          "group relative flex w-full cursor-pointer items-center justify-center overflow-hidden border border-dashed bg-stone-50 text-stone-600 transition",
          "hover:border-amber-500 hover:bg-amber-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2",
          isCircle ? "aspect-square h-32 w-32 rounded-full" : "rounded-xl",
          isDragging && "border-amber-500 bg-amber-50",
          interactionDisabled && "cursor-not-allowed opacity-60",
          hasImage && "border-solid border-stone-300"
        )}
        style={isCircle ? undefined : { aspectRatio }}
      >
        {isProcessing ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <Loader2
              size={isCircle ? 24 : 32}
              className="animate-spin text-stone-500"
              aria-hidden
            />
            {!isCircle ? <div className="text-sm text-stone-600">Preparing your photo…</div> : null}
          </div>
        ) : hasImage ? (
          <>
            <img
              src={displayUrl}
              alt=""
              className={cn(
                "h-full w-full",
                isCircle ? "object-cover" : "object-contain",
                !isCircle && "transition group-hover:opacity-75"
              )}
            />
            {!isCircle ? (
              <div className="pointer-events-none absolute inset-0 flex items-end justify-end p-3 opacity-0 transition group-hover:opacity-100">
                <span className="rounded-md bg-stone-900/80 px-3 py-1.5 text-xs font-medium text-white">
                  Change photo
                </span>
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <ImagePlus
              size={isCircle ? 28 : 36}
              className="text-stone-400 group-hover:text-amber-600"
              aria-hidden
            />
            {!isCircle ? (
              <>
                <div className="text-base font-medium text-stone-800">
                  Drag a photo here, or click to choose
                </div>
                <div className="hidden text-sm text-stone-500 sm:block">
                  JPG, PNG, WebP, or HEIC
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" onClick={openPicker} disabled={interactionDisabled}>
          <Upload size={16} className="mr-2" aria-hidden />
          {hasImage ? "Choose another" : "Choose photo"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={openCamera}
          disabled={interactionDisabled}
          className="sm:hidden"
        >
          <Camera size={16} className="mr-2" aria-hidden />
          Take a picture
        </Button>
        {hasImage && onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            disabled={interactionDisabled}
            className="ml-auto inline-flex items-center text-sm text-stone-500 hover:text-destructive disabled:opacity-50"
          >
            <X size={14} className="mr-1" aria-hidden />
            Remove
          </button>
        ) : null}
      </div>
    </div>
  )
}
